import { useContext } from 'react';
import { createPortal } from 'react-dom';
import BreadcrumbsTargetContext from '@/components/BreadcrumbsTargetContext';
import { getMatchedPath } from '@/routing/getMatchedPath';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { HomeIcon } from '@heroicons/react/24/solid';
import { generatePath, Link, matchPath, useLocation, useParams } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  path: string;
}

export interface Breadcrumb {
  path: string;
  items: BreadcrumbItem[];
}

interface BreadcrumbsProps {
  breadcrumbs: Breadcrumb[];
}

const Breadcrumbs = ({ breadcrumbs }: BreadcrumbsProps) => {
  const target = useContext(BreadcrumbsTargetContext);
  const location = useLocation();
  const params = useParams();
  const currentPath = getMatchedPath(location);

  const result = breadcrumbs.find((item) => item.path === currentPath);
  if (!result || target === null) {
    return;
  }

  const { items } = result;
  const routeParams = { ...params, ...matchPath(result.path, location.pathname)?.params };

  const content = (
    <nav aria-label="breadcrumb">
      <ol className="flex items-center mb-4 space-x-1 md:space-x-1.5">
        <li className="inline-flex items-center">
          <HomeIcon className="w-4 h-4 text-gray-400" />
        </li>
        {items.map((item, index) => {
          const url = generatePath(item.path, routeParams);

          return (
            <li key={index} className="inline-flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mr-1.5" />
              {index === items.length - 1 ? (
                <span className="text-sm font-medium text-gray-500">{item.label}</span>
              ) : (
                <Link to={url} className="text-sm font-medium text-gray-700 hover:text-blue-600 ">
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  return target === undefined ? content : target ? createPortal(content, target) : null;
};

export default Breadcrumbs;
